import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { clearDeliveryNoteRowCache } from '~/components/deliveries/utils';

import { queryKeysDeliveryNote } from '../queryKeys';

import { bulkApproveDlns } from './bulkApproveDlns';
import {
  type BulkApproveDlnsPayload,
  type BulkApproveDlnsResponse,
} from './types';

/**
 * React Query mutation hook for bulk-approving delivery notes.
 *
 * @param options - Optional TanStack Query mutation options for customizing behavior.
 * @returns A mutation object with methods to trigger the bulk approval.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/bulk_approve_dlns_delivery_note_bulk_approve_put
 */
export const useMutationBulkApproveDlns = (
  options?: Omit<
    UseMutationOptions<BulkApproveDlnsResponse, Error, BulkApproveDlnsPayload>,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();
  const { onSettled, ...restOptions } = options ?? {};

  return useMutation({
    mutationFn: (payload: BulkApproveDlnsPayload) => bulkApproveDlns(payload),
    onSettled: (data, error, variables, onMutateResult, context) => {
      if (variables?.assetIds) {
        for (const assetId of variables.assetIds) {
          void queryClient.invalidateQueries({
            queryKey: queryKeysDeliveryNote.getInternal(assetId),
          });
          void queryClient.invalidateQueries({
            queryKey: queryKeysDeliveryNote.get(assetId),
          });
        }
      }

      clearDeliveryNoteRowCache();
      void queryClient.invalidateQueries({
        queryKey: [queryKeysDeliveryNote.base(), 'list'],
        refetchType: 'all',
      });

      void onSettled?.(data, error, variables, onMutateResult, context);
    },
    ...restOptions,
  });
};
